// Card detection: pure function over RGBA pixels (works in browser or Node).
// Returns rotated rectangles {cx, cy, w, h, angle} in the input's pixel space.
function detectCards(data, W, H, opts) {
  opts = opts || {};
  const sens = opts.sensitivity == null ? 50 : opts.sensitivity; // 0..100
  const N = W * H;

  // Estimate background brightness from a thin frame around the scan.
  const samples = [];
  const step = Math.max(1, Math.floor(Math.min(W, H) / 200));
  for (let x = 0; x < W; x += step) { samples.push(px(x, 1)); samples.push(px(x, H - 2)); }
  for (let y = 0; y < H; y += step) { samples.push(px(1, y)); samples.push(px(W - 2, y)); }
  samples.sort((a, b) => a - b);
  const bg = samples[Math.floor(samples.length / 2)];
  function px(x, y) { const i = (y * W + x) * 4; return Math.max(data[i], data[i + 1], data[i + 2]); }

  // Foreground = noticeably darker than the background, or colourful.
  const darkGap = 110 - sens * 0.9;   // sens 50 -> 65
  const satGap = 70 - sens * 0.6;     // sens 50 -> 40
  let mask = new Uint8Array(N);
  for (let p = 0, i = 0; p < N; p++, i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
    const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
    if (mx < bg - darkGap || mx - mn > satGap) mask[p] = 1;
  }

  // Morphological close (dilate then erode) with a square box.
  const rad = Math.max(1, Math.round(Math.min(W, H) / 300));
  mask = erode(dilate(mask, W, H, rad), W, H, rad);

  // Connected components (4-neighbour, iterative).
  const labels = new Int32Array(N);
  const stack = new Int32Array(N);
  const comps = [];
  let next = 1;
  for (let s = 0; s < N; s++) {
    if (!mask[s] || labels[s]) continue;
    let top = 0; stack[top++] = s; labels[s] = next;
    const pts = [];
    while (top) {
      const p = stack[--top];
      const x = p % W, y = (p - x) / W;
      // boundary pixels are enough for the hull
      if (x === 0 || y === 0 || x === W - 1 || y === H - 1 ||
          !mask[p - 1] || !mask[p + 1] || !mask[p - W] || !mask[p + W]) pts.push(x, y);
      if (x > 0 && mask[p - 1] && !labels[p - 1]) { labels[p - 1] = next; stack[top++] = p - 1; }
      if (x < W - 1 && mask[p + 1] && !labels[p + 1]) { labels[p + 1] = next; stack[top++] = p + 1; }
      if (y > 0 && mask[p - W] && !labels[p - W]) { labels[p - W] = next; stack[top++] = p - W; }
      if (y < H - 1 && mask[p + W] && !labels[p + W]) { labels[p + W] = next; stack[top++] = p + W; }
    }
    comps.push({ id: next, pts });
    next++;
  }

  // Strict mask: the printed card itself, ignoring toploader plastic and its shadow.
  const strictDark = bg - 95 - (50 - sens) * 0.6;
  const strictSat = 45 + (50 - sens) * 0.3;
  let strict = new Uint8Array(N);
  for (let p = 0, i = 0; p < N; p++, i += 4) {
    if (!labels[p]) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
    const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
    if (mx < strictDark || mx - mn > strictSat) strict[p] = 1;
  }
  strict = erode(dilate(strict, W, H, rad), W, H, rad);

  const minArea = N * (opts.minAreaFrac || 0.006);
  const out = [];
  for (const c of comps) {
    if (c.pts.length < 40) continue;
    const hull = convexHull(c.pts);
    if (hull.length < 3) continue;
    const outer = minAreaRect(hull);
    const area = outer.w * outer.h;
    if (area < minArea) continue;
    const r = Object.assign({}, outer);
    const inner = innerRect(strict, labels, c.id, W, H, area);
    r.outer = outer;
    r.inner = inner || outer;
    const pick = r.inner;
    r.ratio = Math.max(pick.w, pick.h) / Math.min(pick.w, pick.h);
    r.cardLike = r.ratio > 1.2 && r.ratio < 1.75; // standard card is 1.40
    if (Math.max(outer.w, outer.h) / Math.min(outer.w, outer.h) > 2.2) continue; // long thin junk
    out.push(r);
  }
  return out;
}

// Rectangle around the strongly printed pixels inside one loose component.
function innerRect(strict, labels, id, W, H, outerArea) {
  const seen = new Uint8Array(W * H);
  const stack = [];
  const keep = [];
  let total = 0;
  for (let s = 0; s < W * H; s++) {
    if (!strict[s] || seen[s] || labels[s] !== id) continue;
    const pts = []; let n = 0;
    stack.push(s); seen[s] = 1;
    while (stack.length) {
      const p = stack.pop(); n++;
      const x = p % W, y = (p - x) / W;
      if (x === 0 || y === 0 || x === W - 1 || y === H - 1 ||
          !strict[p - 1] || !strict[p + 1] || !strict[p - W] || !strict[p + W]) pts.push(x, y);
      if (x > 0 && strict[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; stack.push(p - 1); }
      if (x < W - 1 && strict[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; stack.push(p + 1); }
      if (y > 0 && strict[p - W] && !seen[p - W]) { seen[p - W] = 1; stack.push(p - W); }
      if (y < H - 1 && strict[p + W] && !seen[p + W]) { seen[p + W] = 1; stack.push(p + W); }
    }
    if (n > outerArea * 0.02) { for (const v of pts) keep.push(v); total += n; }
  }
  if (total < outerArea * 0.3 || keep.length < 12) return null;
  const hull = convexHull(keep);
  if (hull.length < 3) return null;
  const r = minAreaRect(hull);
  if (r.w * r.h < outerArea * 0.4) return null;
  return r;
}

function dilate(m, W, H, r) { return boxOp(m, W, H, r, true); }
function erode(m, W, H, r) { return boxOp(m, W, H, r, false); }
// Separable box max/min using running counts (outside = 0 for dilate, 1 for erode).
function boxOp(m, W, H, r, isDilate) {
  const tmp = new Uint8Array(W * H), out = new Uint8Array(W * H);
  const pad = isDilate ? 0 : 1, full = 2 * r + 1;
  const pass = (get, set, n, lines) => {
    for (let l = 0; l < lines; l++) {
      let cnt = 0;
      for (let k = -r; k <= r; k++) cnt += (k >= 0 && k < n) ? get(l, k) : pad;
      for (let i = 0; i < n; i++) {
        set(l, i, isDilate ? (cnt > 0 ? 1 : 0) : (cnt === full ? 1 : 0));
        const add = i + r + 1, sub = i - r;
        cnt += (add < n) ? get(l, add) : pad;
        cnt -= (sub >= 0) ? get(l, sub) : pad;
      }
    }
  };
  pass((y, x) => m[y * W + x], (y, x, v) => { tmp[y * W + x] = v; }, W, H);
  pass((x, y) => tmp[y * W + x], (x, y, v) => { out[y * W + x] = v; }, H, W);
  return out;
}

// Andrew's monotone chain on a flat [x0,y0,x1,y1,...] array.
function convexHull(flat) {
  const P = [];
  for (let i = 0; i < flat.length; i += 2) P.push([flat[i], flat[i + 1]]);
  P.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [], upper = [];
  for (const p of P) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
  for (let i = P.length - 1; i >= 0; i--) { const p = P[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

// Minimum-area enclosing rectangle: test each hull edge direction.
function minAreaRect(hull) {
  let best = null;
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i], b = hull[(i + 1) % hull.length];
    const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
    const c = Math.cos(ang), s = Math.sin(ang);
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of hull) {
      const u = p[0] * c + p[1] * s, v = -p[0] * s + p[1] * c;
      if (u < minU) minU = u; if (u > maxU) maxU = u;
      if (v < minV) minV = v; if (v > maxV) maxV = v;
    }
    const area = (maxU - minU) * (maxV - minV);
    if (!best || area < best.area) best = { area, ang, minU, maxU, minV, maxV };
  }
  const { ang, minU, maxU, minV, maxV } = best;
  const c = Math.cos(ang), s = Math.sin(ang);
  const cu = (minU + maxU) / 2, cv = (minV + maxV) / 2;
  let w = maxU - minU + 1, h = maxV - minV + 1, angle = ang;
  // Normalise so |angle| <= 45deg
  while (angle > Math.PI / 4) { angle -= Math.PI / 2; [w, h] = [h, w]; }
  while (angle < -Math.PI / 4) { angle += Math.PI / 2; [w, h] = [h, w]; }
  return { cx: cu * c - cv * s, cy: cu * s + cv * c, w, h, angle };
}

export { detectCards };
